import { AfterViewInit, Component, ViewChild, signal, inject, OnInit } from '@angular/core';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import moment, { Moment } from 'moment';
import { Router } from '@angular/router';
import { ExportDialogComponent } from './export-dialog/export-dialog.component';
import { MaintenanceDashboardService } from './maintenance-dashboard-service/maintenance-dashboard.service';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { HttpStatusCode } from 'axios';
import { MaintenanceListDashboard } from './maintenance-dashboard-interfaces/maintenance-dashboard.interface';
import { MatSortModule,MatSort } from '@angular/material/sort';

/** Matches the DD/MM/YYYY datepicker convention used across the onboarding forms. */
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

/** Date format the API returns for appointment/termination dates. */
const API_DATE_FORMAT = 'YYYY-MM-DD';

@Component({
  selector: 'app-maintenance-dashboard',
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  imports: [
    PiasLayoutComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTableModule,
    MatInputModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
    MatBadgeModule,
    MatChipsModule,
    MatSelectModule,
    MatDialogModule,
    MatDatepickerModule,
    MatSortModule,
  ],
  templateUrl: './maintenance-dashboard.component.html',
  styleUrl: './maintenance-dashboard.component.scss'
})
export class MaintenanceDashboardComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!:MatSort;
 

  setSprinner = signal(false);
  contactsDs = new MatTableDataSource<MaintenanceListDashboard>([]);
  displayedColumns: string[] = ['repcode','repname', 'preferredname', 'branch', 'rnfDate', 'dateResigned', 'repstatus', 'actions'];

  selectedStatus = '';
  searchQuery = '';

  protected get dateFilterFor(): '' | 'Inforced' | 'Resigned' {
    if (this.selectedStatus === 'Inforced' || this.selectedStatus === 'Resigned') {
      return this.selectedStatus;
    }
    return '';
  }

  /** Inforce-only: appointment-date window. */
  appointmentFrom: Moment | null = null;
  appointmentTo: Moment | null = null;

  /** Resigned-only: termination-date window. */
  terminationFrom: Moment | null = null;
  terminationTo: Moment | null = null;

  totalCount = 0;
  inforceCount = 0;
  servingListCount = 0;
  offboardingCount = 0;
  resignedCount = 0;

  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private agentMaintenanceDashboardService = inject(MaintenanceDashboardService);
  private snackBarService = inject(SnackbarService);
  private applicants!: MaintenanceListDashboard[];

  async ngOnInit(): Promise<void> {
    // set the filter in maintanance
    const filterString = sessionStorage.getItem('maintenancefilter');
    const filter = filterString ? JSON.parse(filterString) : null;

    if (filter) {
      this.selectedStatus = filter.selectedStatus;
      if (this.dateFilterFor === 'Inforced') {
        this.appointmentFrom = filter.from;
        this.appointmentTo = filter.to;
      } else if (this.dateFilterFor === 'Resigned') {
        this.terminationFrom = filter.from;
        this.terminationTo = filter.to;
      }
    }
    await this.loadDashboardData();
    // reset the activesectionid 
    const selectedid = {
      sectionid: null
    };
    sessionStorage.setItem('viewSelectedid', JSON.stringify(selectedid));

  }

  ngAfterViewInit(): void {
    this.contactsDs.paginator = this.paginator;
    this.contactsDs.sort = this.sort;
  }

  protected _onSearchRecipients(event: Event): void {
    const searchValue = (event.target as HTMLInputElement).value;
    this.searchQuery = searchValue;
    this.applyFilters();
  }

  protected _clearRecipientSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  /**
   * Status tiles act as toggles — re-clicking the selected tile clears the filter.
   * With the Total card gone, this is the stat row's "show all" affordance.
   */
  protected clickFilters(status: string): void {
    this.selectedStatus = this.selectedStatus === status ? '' : status;
    this.resetDateFilters();
    this.applyFilters();
  }

  protected onStatusFilterChange(): void {
    // ngModel has already written selectedStatus, which drives dateFilterFor.
    this.resetDateFilters();
    this.applyFilters();
  }

  /** Clear button next to the contextual date pickers. */
  protected clearDateFilters(): void {
    this.resetDateFilters();
    this.applyFilters();
  }

  /**
   * Each date filter belongs to exactly one status, so its value is dropped when
   * the status changes — otherwise a filter the user can no longer see would keep
   * narrowing the table.
   */
  private resetDateFilters(): void {
    this.appointmentFrom = null;
    this.appointmentTo = null;
    this.terminationFrom = null;
    this.terminationTo = null;
  }

  protected get hasDateFilter(): boolean {
    return !!(
      this.appointmentFrom || this.appointmentTo ||
      this.terminationFrom || this.terminationTo
    );
  }

  /** Parses an API date string; returns null when absent or malformed. */
  private toMoment(value: string): Moment | null {
    if (!value) {
      return null;
    }
    const parsed = moment(value, API_DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }

  /**
   * Day-wise window test against an API date string — time-of-day never affects a
   * match, and either bound may be null. A missing or malformed date never matches.
   */
  private inDateRange(value: string, from: Moment | null, to: Moment | null): boolean {
    const date = this.toMoment(value);
    if (!date) {
      return false;
    }
    if (from && date.isBefore(from, 'day')) {
      return false;
    }
    if (to && date.isAfter(to, 'day')) {
      return false;
    }
    return true;
  }

  // ── Export ─────────────────────────────────────────────────
  /**
   * Opens the export dialog — rep-status filter plus a per-section column picker.
   * The dialog owns the download itself, so there is no result to handle here;
   * its status filter is deliberately independent of the table's, so an export
   * never silently inherits a narrowing the user set for browsing.
   */
  protected openExportDialog(): void {
    this.dialog.open(ExportDialogComponent, {
      width: '740px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'custom-export-dialog',
    });
  }


  protected applyFilters(): void {
    let filtered = [...this.applicants];

    if (['Inforced', 'Serving Notice', 'Resigned'].includes(this.selectedStatus)) {
      filtered = filtered.filter((applicant) => applicant.repstatus === this.selectedStatus);
    } else if (this.selectedStatus == 'Offboarding') {
      filtered = filtered.filter((applicant) => !!applicant.isOffboarded);
    }

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.trim().toLowerCase();
      filtered = filtered.filter((applicant) =>
        `${applicant.repname} ${applicant.repcode} ${applicant.preferredname} ${applicant.branch} ${applicant.rnfDate} ${applicant.dateResigned}`.toLowerCase().includes(query)
      );
    }

    // Each status filters on its own date field, within the chosen window. Either
    // bound alone is valid. Gated on dateFilterFor so a date filter can only apply
    // while its pickers are on screen.
    if (this.dateFilterFor === 'Inforced' && (this.appointmentFrom || this.appointmentTo)) {
      filtered = filtered.filter((applicant) =>
        this.inDateRange(applicant.rnfDate.split('T')[0], this.appointmentFrom, this.appointmentTo)
      );
    }

    if (this.dateFilterFor === 'Resigned' && (this.terminationFrom || this.terminationTo)) {
      filtered = filtered.filter((applicant) =>
        this.inDateRange(applicant.dateResigned.split('T')[0], this.terminationFrom, this.terminationTo)
      );
    }

    this.contactsDs.data = filtered;

    const filter = {
      selectedStatus: this.selectedStatus,
      from: this.dateFilterFor === 'Inforced' ? this.appointmentFrom : this.terminationFrom,
      to: this.dateFilterFor === 'Inforced' ? this.appointmentTo : this.terminationTo,
    };
    sessionStorage.setItem('maintenancefilter', JSON.stringify(filter));
  }

  async loadDashboardData(): Promise<void> {
    this.setSprinner.set(true);
    const responseData = await this.agentMaintenanceDashboardService
      .getagentMaintenaceList()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {

      this.applicants = responseData.data.responsedata;
      this.contactsDs.data = this.applicants;
      this.totalCount = this.applicants.length;
      this.inforceCount = this.applicants.filter((item) => item.repstatus === 'Inforced').length;
      this.servingListCount = this.applicants.filter((item) => item.repstatus === 'Serving Notice').length;
      // Counted off the flag, not a status — an off-boarding rep is also counted in
      // Inforce or Serving Notice, so the tiles deliberately overlap here.
      this.offboardingCount = this.applicants.filter((item) => !!item.isOffboarded).length;
      this.resignedCount = this.applicants.filter((item) => item.repstatus === 'Resigned').length;


      this.applyFilters();

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  goToView(applicant: MaintenanceListDashboard): void {
    this.router.navigate(['agent-maintenance/agent-maintenance-form'], {
      state: {
        applicant,
        isReadOnly: true,
      }
    });
  }

  goToEdit(applicant: MaintenanceListDashboard): void {
    this.router.navigate(['agent-maintenance/agent-maintenance-form'], {
      state: {
        applicant,
        isReadOnly: false,
      }
    });
  }

  canView(status: string): boolean {
    return ['Inforced', 'Serving Notice', 'Resigned'].includes(status);
  }

  /**
   * Conic-gradient ring for a stat tile: the accent sweeps to the status's share
   * of the total, then the track fills the remainder. The `0` stop is clamped up
   * to the preceding position, which is what produces the hard edge.
   */
  protected ringGradient(value: number): string {
    return `conic-gradient(var(--c1) ${this.pctOf(value)}, var(--track) 0)`;
  }

  /** Share of the total representative count, formatted for the stat-card caption. */
  pctOf(value: number): string {
    if (!this.totalCount) {
      return '0%';
    }
    return Math.round((value / this.totalCount) * 100) + '%';
  }

  // Palette mirrors the stat-card accent colors so avatars feel part of the same system.
  private readonly avatarPalette = [
    { bg: '#e8eaf6', color: '#3f51b5' },
    { bg: '#e3f2fd', color: '#1565c0' },
    { bg: '#fff3e0', color: '#ef6c00' },
    { bg: '#e8f5e9', color: '#2e7d32' },
    { bg: '#f3e5f5', color: '#6a1b9a' },
    { bg: '#e0f2f1', color: '#00897b' },
    { bg: '#fce4ec', color: '#c2185b' }
  ];

  getAvatarStyle(fullName: string): { bg: string; color: string } {
    const key = (fullName || '').trim();
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return this.avatarPalette[hash % this.avatarPalette.length];
  }

  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName
      .trim()
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  getStatusClass(status: string): string {
    switch ((status || '').trim()) {
      case 'Inforced':
        return 'status-active';
      case 'Serving Notice':
        return 'status-notice';
      case 'Resigned':
        return 'status-resigned';
      default:
        return 'status-default';
    }
  }
}
